import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

const DBSchema = () => {
  const { user } = useAuth();
  const [schema, setSchema] = useState([]);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [expandedTable, setExpandedTable] = useState(null);
  const totalFields = useMemo(
    () => schema.reduce((sum, table) => sum + (table.columns?.length || 0), 0),
    [schema]
  );

  const fetchSchema = async () => {
    setSchemaLoading(true);
    try {
      const response = await axios.get('/api/settings/db-schema');
      setSchema(response.data);
    } catch (error) {
      console.error('Error fetching schema:', error);
    } finally {
      setSchemaLoading(false);
    }
  };

  useEffect(() => {
    if (user?.isSuperAdmin) {
      fetchSchema();
    }
  }, [user]);

  const getKeyBadge = (key) => {
    if (key === 'PRI') return <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-yellow-100 text-yellow-800">PK</span>;
    if (key === 'MUL') return <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-blue-100 text-blue-800">FK</span>;
    if (key === 'UNI') return <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-purple-100 text-purple-800">UNQ</span>;
    return null;
  };

  return (
    <Layout>
      <div className="px-4 py-6 sm:px-0">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Database Schema</h1>

        {/* DB Schema Viewer - Super Admin Only */}
        {user?.isSuperAdmin && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <h2 className="text-lg font-semibold text-gray-900">Database Schema</h2>
                <span className="text-sm font-medium text-gray-600">
                  Total fields: {totalFields}
                </span>
              </div>
              <button
                onClick={fetchSchema}
                disabled={schemaLoading}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {schemaLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {schema.length === 0 && !schemaLoading && (
              <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">No tables found.</div>
            )}

            <div className="space-y-3">
              {schema.map((table) => (
                <div key={table.name} className="bg-white shadow rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedTable(expandedTable === table.name ? null : table.name)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 text-left"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-semibold text-gray-900 font-mono">{table.name}</span>
                      <span className="text-xs text-gray-500">{table.columns.length} columns</span>
                      <span className="text-xs text-gray-400">~{table.rows || 0} rows</span>
                    </div>
                    <span className="text-gray-400 text-sm">{expandedTable === table.name ? '▲' : '▼'}</span>
                  </button>

                  {expandedTable === table.name && (
                    <div className="border-t border-gray-200">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Column</th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nullable</th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Default</th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Key</th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Extra</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {table.columns.map((col) => (
                            <tr key={col.name} className="hover:bg-gray-50">
                              <td className="px-6 py-2 text-sm font-mono text-gray-900">{col.name}</td>
                              <td className="px-6 py-2 text-sm font-mono text-gray-600">{col.type}</td>
                              <td className="px-6 py-2 text-sm text-gray-500">{col.nullable ? 'Yes' : 'No'}</td>
                              <td className="px-6 py-2 text-sm font-mono text-gray-500">{col.default ?? '-'}</td>
                              <td className="px-6 py-2 text-sm">{getKeyBadge(col.key)}</td>
                              <td className="px-6 py-2 text-sm text-gray-500">{col.extra || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default DBSchema;
